import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod/v3";
import {
  type SsoSettings,
  useAddSsoDomain,
  useInviteItContact,
  useRemoveSsoDomain,
  useSaveSsoSettings,
  useSyncSsoDomains,
  useWorkspaceSso,
} from "@/api/admin/workspace-sso";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Toggle } from "@/components/base/toggle/toggle";
import { TeamConfirmModal } from "@/components/team/team-confirm-modal";
import { TabHeader } from "@/pages/admin/merchant-detail/tabs/tab-header";
import { success } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";

const DEFAULT_SETTINGS: SsoSettings = {
  enabled: false,
  enforceSso: false,
  allowJitProvisioning: true,
  providerName: null,
};

const inputClass =
  "w-full px-3 py-2 text-md text-primary bg-primary rounded-lg ring-1 ring-primary outline-none focus:ring-2 focus:ring-blue-500";

const Row = ({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) => (
  <>
    <div className="border-t border-primary" />
    <div className="flex flex-row gap-8">
      <div className="w-80 flex flex-col gap-1">
        <p className="text-sm font-medium text-primary">{title}</p>
        <p className="text-sm text-tertiary">{hint}</p>
      </div>
      <div className="flex-1 max-w-md flex flex-col gap-3">{children}</div>
    </div>
  </>
);

const domainSchema = z.object({ domain: z.string().trim().min(1) });
const itContactSchema = z.object({ email: z.string().trim().email() });

export const SsoTab = ({ workspace }: { workspace: { id: string } }) => {
  const { data, isLoading } = useWorkspaceSso(workspace.id);
  const saveSettings = useSaveSsoSettings(workspace.id);
  const addDomain = useAddSsoDomain(workspace.id);
  const removeDomain = useRemoveSsoDomain(workspace.id);
  const syncDomains = useSyncSsoDomains(workspace.id);
  const inviteItContact = useInviteItContact(workspace.id);

  const config = data?.config ?? null;
  const savedSettings: SsoSettings = config
    ? {
        enabled: config.enabled,
        enforceSso: config.enforceSso,
        allowJitProvisioning: config.allowJitProvisioning,
        providerName: config.providerName,
      }
    : DEFAULT_SETTINGS;
  const settingsForm = useForm<SsoSettings>({ values: savedSettings });
  const domainForm = useForm<z.infer<typeof domainSchema>>({
    resolver: zodResolver(domainSchema),
    defaultValues: { domain: "" },
  });
  const itContactForm = useForm<z.infer<typeof itContactSchema>>({
    resolver: zodResolver(itContactSchema),
    defaultValues: { email: "" },
  });
  const [confirmingEnforcement, setConfirmingEnforcement] = useState(false);

  if (isLoading) return <p className="text-sm text-tertiary">Loading...</p>;

  const domains = data?.domains ?? [];

  const save = () =>
    saveSettings.mutate(settingsForm.getValues(), {
      onSuccess: ({ revokedSessions }) =>
        success(
          revokedSessions ? `SSO saved. ${pluralize(revokedSessions, "session", "sessions")} signed out.` : "SSO saved",
        ),
      onSettled: () => setConfirmingEnforcement(false),
    });

  const handleSave = settingsForm.handleSubmit((settings) =>
    settings.enforceSso && !savedSettings.enforceSso ? setConfirmingEnforcement(true) : save(),
  );

  const cancelEnforcement = (open: boolean) => {
    if (open) return;
    setConfirmingEnforcement(false);
    settingsForm.setValue("enforceSso", savedSettings.enforceSso, { shouldDirty: true });
  };

  const handleAddDomain = domainForm.handleSubmit(({ domain }) =>
    addDomain.mutate(domain, { onSuccess: () => domainForm.reset() }),
  );

  const handleInvite = itContactForm.handleSubmit(({ email }) =>
    inviteItContact.mutate(
      { email, intents: ["sso", "domain_verification"] },
      {
        onSuccess: () => {
          itContactForm.reset();
          success("WorkOS sent the setup invitation");
        },
      },
    ),
  );

  return (
    <div className="flex flex-col gap-6">
      <TeamConfirmModal
        isOpen={confirmingEnforcement}
        onOpenChange={cancelEnforcement}
        title="Require single sign-on?"
        description="Everyone on this workspace's domains is signed out and can no longer use a password."
        confirmLabel="Require SSO"
        onConfirm={save}
        isPending={saveSettings.isPending}
      />
      <TabHeader
        title="Single sign-on"
        subtitle="Let this workspace's people sign in through their company's identity provider, via WorkOS"
        hasChanges={settingsForm.formState.isDirty}
        isPending={saveSettings.isPending}
        onSave={handleSave}
        onCancel={() => settingsForm.reset()}
      />

      <Row title="Status" hint="Domains only route to SSO while it is enabled and once WorkOS has verified them">
        <Controller
          control={settingsForm.control}
          name="enabled"
          render={({ field }) => <Toggle label="Enabled" isSelected={field.value} onChange={field.onChange} />}
        />
        <Controller
          control={settingsForm.control}
          name="enforceSso"
          render={({ field }) => (
            <Toggle
              label="Require SSO"
              hint="Turns off passwords for everyone on these domains"
              isSelected={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={settingsForm.control}
          name="allowJitProvisioning"
          render={({ field }) => (
            <Toggle
              label="Create accounts on first sign-in"
              hint="Otherwise people need an invitation before they can use SSO"
              isSelected={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Row>

      <Row title="Provider name" hint="An internal label for this connection. People signing in don't see it">
        <Controller
          control={settingsForm.control}
          name="providerName"
          render={({ field }) => (
            <input
              type="text"
              aria-label="Provider name"
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value || null)}
              className={inputClass}
            />
          )}
        />
      </Row>

      {config && (
        <Row title="Domains" hint="Email domains that sign in through this workspace's SSO">
          {domains.map((domain) => (
            <div key={domain.id} className="flex flex-row items-center justify-between gap-3">
              <span className="text-md text-primary">{domain.domain}</span>
              <div className="flex flex-row items-center gap-3">
                {domain.verifiedAt ? <Badge color="success">Verified</Badge> : <Badge color="gray">Unverified</Badge>}
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={removeDomain.isPending}
                  onClick={() => removeDomain.mutate(domain.id)}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <form className="flex flex-row gap-3" onSubmit={handleAddDomain}>
            <input
              type="text"
              aria-label="New domain"
              placeholder="acme.com"
              {...domainForm.register("domain")}
              className={inputClass}
            />
            <Button
              type="submit"
              size="md"
              color="secondary"
              isDisabled={!domainForm.watch("domain") || addDomain.isPending}
            >
              Add
            </Button>
          </form>
          {domains.length > 0 && (
            <Button size="sm" color="secondary" isLoading={syncDomains.isPending} onClick={() => syncDomains.mutate()}>
              Check verification with WorkOS
            </Button>
          )}
        </Row>
      )}

      {config && (
        <Row
          title="WorkOS"
          hint="The customer's IT team connects their identity provider and verifies their domains in the WorkOS Admin Portal"
        >
          <p className="text-sm text-tertiary">Organization {config.workosOrganizationId}</p>
          <form className="flex flex-row gap-3" onSubmit={handleInvite}>
            <input
              type="email"
              aria-label="IT contact email"
              placeholder="it@acme.com"
              {...itContactForm.register("email")}
              className={inputClass}
            />
            <Button
              type="submit"
              size="md"
              color="secondary"
              isDisabled={!itContactForm.watch("email") || inviteItContact.isPending}
            >
              Send setup invitation
            </Button>
          </form>
        </Row>
      )}

      {data && data.events.length > 0 && (
        <Row title="Recent activity" hint="The last changes to this workspace's SSO">
          {data.events.map((event) => (
            <p key={event.id} className="text-sm text-tertiary">
              {new Date(event.createdAt).toLocaleString()} · {event.eventType.replaceAll("_", " ")}
            </p>
          ))}
        </Row>
      )}
    </div>
  );
};
